import React from 'react';
import { formatDate } from '@/utils/date';
import { Prompt } from '@/hooks/usePrompt';
import { useLanguage } from '@/contexts';
import AboutSection from './AboutSection';
import { Model } from '@/hooks/useModels';

interface RightSidebarProps {
  prompt: Prompt;
  models: Model[];
  totalCopies: number;
  isOwner: boolean;
  isPublic: boolean;
  onToggleVisibility: () => void;
  onCopyText: (text: string, type: string) => void;
}

const RightSidebar: React.FC<RightSidebarProps> = ({
  prompt,
  models,
  totalCopies,
  isOwner,
  isPublic,
  onToggleVisibility,
  onCopyText,
}) => {
  const { t } = useLanguage();

  // Find the model that matches prompt.model_type
  const model = prompt?.model_type 
    ? models.find((m) => m.name === prompt.model_type)
    : null;

  // Format model display name with provider
  const capitalizeProvider = (provider: string): string => {
    if (!provider) return provider;
    return provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase();
  };

  const modelDisplayName = model 
    ? `${capitalizeProvider(model.provider)}/${model.name}`
    : prompt?.model_type 
      ? prompt.model_type 
      : 'No model specified';

  // Format tokens display (input/output)
  const tokensDisplay = model?.context_window 
    ? `${model.context_window.toLocaleString()} tokens`
    : 'N/A';

  // Format cost display (costs are stored per million tokens, convert to per 1K tokens for display)
  const inputCost = model?.input_cost_per_million_tokens !== null && model?.input_cost_per_million_tokens !== undefined
    ? (model.input_cost_per_million_tokens / 1000).toFixed(2)
    : '0.00';
  
  const outputCost = model?.output_cost_per_million_tokens !== null && model?.output_cost_per_million_tokens !== undefined
    ? (model.output_cost_per_million_tokens / 1000).toFixed(2)
    : '0.00';
  
  const costDisplay = `$${inputCost} / $${outputCost}`;

  return (
    <div className="w-full lg:w-80 p-3 sm:p-4 md:p-6 bg-gray-50 dark:bg-slate-900 border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-slate-700 space-y-4 sm:space-y-6 transition-colors duration-300">
      {/* Model Selection */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100">{t("model")}</h3>
          <button
            onClick={() => onCopyText(modelDisplayName, 'Model')}
            className="flex items-center text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 text-xs transition-colors duration-200"
            aria-label="Copy model"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span className="ml-1">{t("copy")}</span>
          </button>
        </div>
        
        {/* Dynamic Content */}
        <div className="text-sm font-medium text-gray-900 dark:text-slate-100 mb-1">{modelDisplayName}</div>
        <div className="text-xs text-gray-500 dark:text-slate-400 mb-2">{tokensDisplay}</div>
        <div className="text-xs text-gray-600 dark:text-slate-400">{costDisplay}</div>
      </div>

      {/* About Section */}
      <AboutSection
        prompt={prompt}
        totalCopies={totalCopies}
        isOwner={isOwner}
        isPublic={isPublic}
        onToggleVisibility={onToggleVisibility}
        formatDate={formatDate}
      />

      {/* Action Buttons */}
      <div className="space-y-3">
        {/* Agrega tus botones de acci�n aqu� si es necesario */}
      </div>
    </div>
  );
};

export default RightSidebar;